import React, { useCallback } from "react";
import { useIntl } from "../hooks/useIntl";
import moment from "moment";
import { getAssetURL } from "../helpers/assets";
import { getTranslations } from "../helpers/translations";
import ArticleDetailMain, {
  ArticleDetailMainProps,
} from "../components/ArticleDetailMain";

import Page from "../components/Page";
import SEO from "../components/SEO";
import { parseSEO } from "../helpers/seo";
import { ROUTES } from "../helpers/routes";
import { OG_TYPES } from "../components/SEO/SEO";
import { useStaticLabels } from "../hooks/useStaticLabels";

type tagType = {
  article_tag_id: articleTagType[];
};

type articleTagType = {
  translations: translationsTagType[];
};

type translationsTagType = {
  tag_name: string;
};

const Article = ({ pageContext }: { pageContext: any }) => {
  const {data, article_slug, article_type} = pageContext;
  const intl = useIntl();
  const labels = useStaticLabels();

  const getRootTranslatedData = useCallback(() => {
    const translatedArticle =
      getTranslations(data, "translations", intl.locale) ?? {};
    return {
      ...translatedArticle,
      article_slug,
      article_type,
    };
  }, [data]);

  const getArticleDetailMainData = useCallback((): ArticleDetailMainProps => {
    const translatedData = getRootTranslatedData();

    const translatedTypeData = getTranslations(
      translatedData,
      "article_type.translations",
      intl.locale
    );

    const articleType = data?.directus?.article_by_id?.article_type?.type_value;

    const tagsArray: string[] = [];

    translatedData?.tags?.forEach((tag: tagType) => {
      const translatedData = getTranslations(
        tag,
        "article_tag_id.translations",
        intl.locale
      );
      tagsArray.push(translatedData?.tag_name);
    });

    const fromBlogs = articleType === "blog";

    let backInfo = {
      label: labels?.backToAllBlogsLabel,
      link: `${ROUTES.NEWSROOM}#${articleType}`,
    };
    if (!fromBlogs) {
      backInfo = {
        label: labels?.backToAllPressReleasesLabel,
        link: `${ROUTES.NEWSROOM}#${articleType}`,
      };
    }

    return {
      image: {
        src: getAssetURL(translatedData?.article_image),
        alt: translatedData?.article_image_alt,
      },
      thumbnail: {
        src: getAssetURL(translatedData?.article_thumbnail),
        alt: translatedData?.article_thumbnail_alt,
      },
      back: backInfo,
      type: translatedTypeData?.article_type_label,
      tags: tagsArray,
      title: translatedData?.article_title,
      date: moment(translatedData?.article_date).format("MMMM D, YYYY"),
      content: translatedData?.article_content,
      faqTitle: translatedData?.article_questions_title,
      faq: translatedData?.article_questions_accordion,
      file: translatedData?.article_file
        ? getAssetURL(translatedData?.article_file)
        : "",
    };
  }, [getRootTranslatedData]);

  const articleData = getArticleDetailMainData();

  return (
    <Page
      initialHeaderBackgroundColor={"var(--color-green-90)"}
      helmet={
        <SEO
          {...parseSEO(getRootTranslatedData(), intl?.locale, {
            og_image: articleData?.image?.src,
            og_type: OG_TYPES.article,
          })}
        />
      }
    >
      <ArticleDetailMain {...articleData} />
    </Page>
  );
};

export default Article;